import axios from 'axios';

// Determine API URL based on environment
function getApiUrl() {
  // Allow explicit override via environment variable
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  
  // In production builds, NODE_ENV is set to 'production' at build time
  // Production mode = frontend and backend on same server/port
  // Always use relative URLs when in production build
  if (process.env.NODE_ENV === 'production') {
    return ''; // Same server - use relative URLs
  }
  
  // Development mode: React dev server on port 3000, API on port 5000
  // When accessing via localhost, React dev server proxy handles API calls
  // When accessing via IP address, need explicit backend URL
  const hostname = window.location.hostname;
  
  // If accessing via localhost/127.0.0.1, React dev server proxy will handle it
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return ''; // Use relative URLs, proxy will forward to :5000
  }
  
  // If accessing via IP address (network access), backend is on port 5000
  // This is for development mode when accessing from another device
  return `http://${hostname}:5000`;
}

const API_URL = getApiUrl();

// Create axios instance
const api = axios.create({
  baseURL: `${API_URL}/api`,
  timeout: 30000,
});

// Fetch gates
export const fetchGates = async () => {
  try {
    const response = await api.get('/gates');
    return response.data;
  } catch (error) {
    console.error('Error fetching gates:', error);
    throw error;
  }
};

// Fetch gate crossings
export const fetchGateCrossings = async () => {
  try {
    const response = await api.get('/gate-crossings');
    return response.data;
  } catch (error) {
    console.error('Error fetching gate crossings:', error);
    throw error;
  }
};

// Fetch activities with query parameters
export const fetchActivities = async (params = {}) => {
  try {
    const response = await api.get('/activities', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching activities:', error);
    throw error;
  }
};

// Fetch activity summaries (metadata with point counts, no GPS data)
export const fetchActivitySummaries = async (params = {}) => {
  try {
    const response = await api.get('/activities/summaries', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching activity summaries:', error);
    throw error;
  }
};

// Locations API
export const fetchLocations = async () => {
  try {
    const response = await api.get('/locations');
    return response.data;
  } catch (error) {
    console.error('Error fetching locations:', error);
    throw error;
  }
};

// Device API functions
export const fetchDevices = async () => {
  try {
    const response = await api.get('/devices');
    return response.data;
  } catch (error) {
    console.error('Error fetching devices:', error);
    throw error;
  }
};

export const fetchDeviceStatus = async (deviceId) => {
  try {
    const response = await api.get(`/devices/${deviceId}/status`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching device status for ${deviceId}:`, error);
    throw error;
  }
};

export const fetchDeviceQueue = async (deviceId) => {
  try {
    const response = await api.get(`/devices/${deviceId}/queue`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching device queue for ${deviceId}:`, error);
    throw error;
  }
};

export const fetchDeviceHealthLogs = async (deviceId, limit = 1000, limitTimestamp = null) => {
  try {
    const params = { limit };
    if (limitTimestamp) {
      params.limitTimestamp = limitTimestamp;
    }
    const response = await api.get(`/devices/${deviceId}/health-logs`, { params });
    return response.data;
  } catch (error) {
    console.error(`Error fetching device health logs for ${deviceId}:`, error);
    throw error;
  }
};

// Fetch recent media for a device (queried by capturePoint_id, ordered by createdAt desc)
export const fetchDeviceMedia = async (deviceId, limit = 25) => {
  try {
    const response = await api.get(`/devices/${deviceId}/media`, { params: { limit } });
    return response.data;
  } catch (error) {
    console.error(`Error fetching device media for ${deviceId}:`, error);
    throw error;
  }
};

// Fetch device configuration
export const fetchDeviceConfig = async (deviceId) => {
  try {
    const response = await api.get(`/devices/${deviceId}/config`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching device config for ${deviceId}:`, error);
    throw error;
  }
};

// Update device configuration
export const updateDeviceConfig = async (deviceId, updates) => {
  try {
    const response = await api.patch(`/devices/${deviceId}/config`, updates);
    return response.data;
  } catch (error) {
    console.error(`Error updating device config for ${deviceId}:`, error);
    throw error;
  }
};

// Fetch all gate IDs for selection
export const fetchAllGateIds = async () => {
  try {
    const response = await api.get('/gates/ids');
    return response.data;
  } catch (error) {
    console.error('Error fetching gate IDs:', error);
    throw error;
  }
};

// Fetch all device gate assignments (which gates are assigned to which devices)
export const fetchGateAssignments = async () => {
  try {
    const response = await api.get('/gates/assignments');
    return response.data;
  } catch (error) {
    console.error('Error fetching gate assignments:', error);
    throw error;
  }
};

// Update gate GPS coordinates
export const updateGateCoordinates = async (gateId, coordinates) => {
  try {
    const response = await api.patch(`/gates/${gateId}/coordinates`, { coordinates });
    return response.data;
  } catch (error) {
    console.error(`Error updating gate coordinates for ${gateId}:`, error);
    throw error;
  }
};

// Fetch video for a gate crossing
export const fetchCrossingVideo = async (gateId, activityId) => {
  try {
    const response = await api.get('/crossing-video', { params: { gateId, activityId } });
    return response.data;
  } catch (error) {
    console.error('Error fetching crossing video:', error);
    throw error;
  }
};

// Trigger a manual gate crossing (for ops/testing)
export const triggerCrossing = async (deviceId) => {
  try {
    const response = await api.post('/trigger-crossing', { deviceId });
    return response.data;
  } catch (error) {
    console.error('Error triggering crossing:', error);
    throw error;
  }
};

// ── ERD Document API ──

export const fetchERDDocuments = async () => {
  try {
    const response = await api.get('/erd');
    return response.data;
  } catch (error) {
    console.error('Error fetching ERD documents:', error);
    throw error;
  }
};

export const fetchERDDocument = async (id) => {
  try {
    const response = await api.get(`/erd/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching ERD document:', error);
    throw error;
  }
};

export const createERDDocument = async (data) => {
  try {
    const response = await api.post('/erd', data);
    return response.data;
  } catch (error) {
    console.error('Error creating ERD document:', error);
    throw error;
  }
};

export const updateERDDocument = async (id, data) => {
  try {
    const response = await api.put(`/erd/${id}`, data);
    return response.data;
  } catch (error) {
    console.error('Error updating ERD document:', error);
    throw error;
  }
};

export const deleteERDDocument = async (id) => {
  try {
    const response = await api.delete(`/erd/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting ERD document:', error);
    throw error;
  }
};

export const restoreERDDocument = async (id) => {
  try {
    const response = await api.put(`/erd/${id}/restore`);
    return response.data;
  } catch (error) {
    console.error('Error restoring ERD document:', error);
    throw error;
  }
};

export const fetchERDRevisions = async (id) => {
  try {
    const response = await api.get(`/erd/${id}/revisions`);
    return response.data;
  } catch (error) {
    console.error('Error fetching ERD revisions:', error);
    throw error;
  }
};

export default api;

